/* components/onboarding/OnboardingDialog.jsx ---------------------------
   The presentation of QRProofs' three main features - the QR code, the
   statistics, the events sent to YummiTracker - in three steps. The
   dashboard opens it by itself at the owner's first login (while
   owner.onboarding_seen_at is NULL) and on its « Découvrir QRProofs »
   button. Closing it, by any way, calls onClose.
-------------------------------------------------------------------- */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import counterKioskImg from '../../assets/onboarding-counter-kiosk.jpg';
import StatsPreview from './StatsPreview';
import EventsPreview from './EventsPreview';

// Each step names the dashboard button it is reached from (dashboard.*).
// A photo fills the dialog's width; a preview is drawn (StatsPreview.jsx,
// EventsPreview.jsx).
const STEPS = [
  { key: 'qr', image: counterKioskImg, button: 'dashboard.showUrl' },
  { key: 'stats', Preview: StatsPreview, button: 'dashboard.viewStats' },
  { key: 'events', Preview: EventsPreview, button: 'dashboard.description' },
];

export default function OnboardingDialog({ open, onClose }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);

  useEffect(() => { if (open) setStep(0); }, [open]);

  const current = STEPS[step];
  const last = step === STEPS.length - 1;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="overline" color="text.secondary" component="div">
          {t('onboarding.kicker', { step: step + 1, total: STEPS.length })}
        </Typography>
        {t(`onboarding.${current.key}Title`)}
      </DialogTitle>

      <DialogContent dividers>
        {current.Preview ? <current.Preview /> : (
          <Box
            component="img"
            src={current.image}
            alt=""
            sx={{ display: 'block', width: '100%', aspectRatio: '3 / 2', objectFit: 'cover', mb: 2, borderRadius: 2 }}
          />
        )}
        <Typography sx={{ mb: 1.5 }}>{t(`onboarding.${current.key}Text`)}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('onboarding.where', { button: t(current.button) })}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Box sx={{ display: 'flex', gap: 0.75 }} aria-hidden="true">
          {STEPS.map((s, i) => (
            <Box
              key={s.key}
              sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: i === step ? 'primary.main' : 'action.disabled' }}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!last && <Button onClick={onClose}>{t('onboarding.skip')}</Button>}
          {step > 0 && <Button onClick={() => setStep(step - 1)}>{t('onboarding.back')}</Button>}
          {last
            ? <Button variant="contained" onClick={onClose}>{t('onboarding.done')}</Button>
            : <Button variant="contained" onClick={() => setStep(step + 1)}>{t('onboarding.next')}</Button>}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
